import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Pencil, RefreshCw, X } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { ToolResultPageRenderer } from '@/components/tools/ToolResultPageRenderer';
import { buildToolAppComposition } from '@shared/tool-app-composition';
import { ToolAppCommunicationPanel } from '@/components/tool-apps/ToolAppCommunicationPanel';
import type { DashboardUiError } from '@/lib/dashboard-errors';
import { invokeToolAppExecute } from '@/lib/tool-app-execute';
import { supabase } from '@/lib/supabase';
import { useRegisterAppAssistantContext } from '@/lib/app-assistant-context';
import { cn } from '@/lib/utils';
import type { ToolAppExecuteResponse } from '@shared/types/tool-apps';

const readFiltersFromSearchParams = (searchParams: URLSearchParams): Record<string, string> => {
  const filters: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    if (key.startsWith('filter.') && key.length > 'filter.'.length) {
      filters[key.slice('filter.'.length)] = value;
    }
  });
  return filters;
};

export const ToolAppPage = () => {
  const navigate = useNavigate();
  const { toolAppKey } = useParams<{ toolAppKey: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [runtime, setRuntime] = useState<ToolAppExecuteResponse | null>(null);
  const [error, setError] = useState<DashboardUiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [draftFilters, setDraftFilters] = useState<Record<string, string>>({});
  const [canManage, setCanManage] = useState(false);
  const surfaceKey = searchParams.get('surface');
  const searchSignature = searchParams.toString();
  const activeFilters = readFiltersFromSearchParams(searchParams);

  const loadToolApp = async () => {
    if (!toolAppKey) {
      setError({
        message: 'Cet outil est introuvable.',
        code: null,
        recoveryActions: [],
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setCanManage(false);
    try {
      const response = await invokeToolAppExecute({
        toolAppKey,
        surfaceKey,
        filters: activeFilters,
      });
      setRuntime(response);
      const { data: managePermission } = await supabase.rpc('can_manage_tool_app', {
        p_tool_app_id: response.toolApp.id,
      });
      setCanManage(managePermission === true);
      setDraftFilters(Object.fromEntries((response.surface.filters ?? []).map((filter) => [
        filter.key,
        activeFilters[filter.key] ?? filter.defaultValue ?? '',
      ])));
    } catch (loadError) {
      setError(loadError as DashboardUiError);
      setRuntime(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadToolApp();
  }, [toolAppKey, searchSignature]);

  const selectSurface = (nextSurfaceKey: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('surface', nextSurfaceKey);
      for (const key of Array.from(next.keys())) {
        if (key.startsWith('filter.')) next.delete(key);
      }
      return next;
    });
  };

  const applyFilters = () => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const key of Array.from(next.keys())) {
        if (key.startsWith('filter.')) next.delete(key);
      }
      for (const [key, value] of Object.entries(draftFilters)) {
        const trimmedValue = value.trim();
        if (trimmedValue) next.set(`filter.${key}`, trimmedValue);
      }
      return next;
    });
  };

  const resetFilters = () => {
    setDraftFilters(Object.fromEntries((runtime?.surface.filters ?? []).map((filter) => [
      filter.key,
      filter.defaultValue ?? '',
    ])));
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const key of Array.from(next.keys())) {
        if (key.startsWith('filter.')) next.delete(key);
      }
      return next;
    });
  };

  const assistantContext = useMemo(() => ({
    surface: 'home' as const,
    routePath: `/apps/${toolAppKey ?? ''}`,
    routeLabel: runtime?.toolApp.title ?? 'Application',
    visibleSummary: runtime ? `${runtime.toolApp.description ?? ''}\n${JSON.stringify(Object.values(runtime.viewEnvelopesByBindingId).map((view) => view?.data)).slice(0, 6000)}` : 'Application en cours de chargement.',
    visibleEntities: runtime?.surface.views.flatMap((view) => view.viewDefinition.displayMeta.title ?? []) ?? [],
    visibleError: error?.message ?? null,
  }), [toolAppKey, runtime, error]);
  useRegisterAppAssistantContext(assistantContext);

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center px-6 py-12">
        <div className="text-center">
          <RefreshCw className="mx-auto h-5 w-5 animate-spin text-gray-400" />
          <p className="mt-3 text-sm text-domino-inkSoft">Chargement de l’application…</p>
        </div>
      </div>
    );
  }

  if (error || !runtime) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Card className="rounded-[28px] border-red-200 bg-red-50 p-6">
          <p className="text-sm font-semibold text-red-900">Application indisponible</p>
          <p className="mt-2 text-sm leading-6 text-red-700">
            {error?.message ?? 'Impossible d’afficher cet outil pour le moment.'}
          </p>
          <div className="mt-5 flex gap-3">
            <Button onClick={() => void loadToolApp()}>Réessayer</Button>
            <Button variant="secondary" onClick={() => navigate('/tools')}>Retour aux applications</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
      <button
        type="button"
        onClick={() => navigate('/tools')}
        className="inline-flex items-center gap-2 text-sm font-medium text-domino-inkSoft transition-colors hover:text-domino-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Applications
      </button>

      <Card noPadding className="mt-4 overflow-hidden border-domino-cardBorder bg-domino-white">
        <div className="border-b border-domino-cardBorder px-5 py-5 lg:px-6">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="max-w-3xl">
              <h1 className="font-display text-2xl font-semibold tracking-tight text-domino-ink">
                {runtime.toolApp.title}
              </h1>
              {(runtime.toolApp.description ?? runtime.definition.description) && (
                <p className="mt-2 text-sm leading-6 text-domino-inkSoft">
                  {runtime.toolApp.description ?? runtime.definition.description}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {canManage && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    navigate(`/forge?toolAppKey=${runtime.toolApp.key}`)
                  }
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Modifier
                </Button>
              )}
              <Button variant="secondary" onClick={() => void loadToolApp()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Actualiser
              </Button>
            </div>
          </div>
        </div>

        {runtime.definition.surfaces.length > 1 && (
          <div className="flex gap-2 overflow-x-auto border-b border-gray-100 px-6 py-3 lg:px-8">
            {runtime.definition.surfaces.map((surface) => (
              <button
                key={surface.key}
                type="button"
                onClick={() => selectSurface(surface.key)}
                className={cn(
                  'whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors',
                  surface.key === runtime.surface.key
                    ? 'bg-domino-aubergine text-white'
                    : 'bg-gray-100 text-domino-inkSoft hover:bg-gray-200 hover:text-domino-ink',
                )}
              >
                {surface.title}
              </button>
            ))}
          </div>
        )}

        <div className="py-5">
          {(runtime.surface.filters?.length ?? 0) > 0 && (
            <details className="mx-4 mb-5 rounded-xl border border-domino-cardBorder p-3 sm:mx-5 lg:mx-6">
              <summary className="cursor-pointer text-sm font-medium text-domino-ink">Filtres{Object.keys(activeFilters).length ? ` (${Object.keys(activeFilters).length})` : ''}</summary>
              <div className="flex flex-wrap items-center justify-between gap-3">
                {Object.keys(activeFilters).length > 0 && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-domino-inkSoft hover:text-domino-ink"
                  >
                    <X className="h-3.5 w-3.5" />
                    Réinitialiser
                  </button>
                )}
              </div>

              <p className="mt-1 text-xs text-domino-inkSoft">Les filtres concernent les tableaux chargés, sans modifier les indicateurs de synthèse.</p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                {runtime.surface.filters?.map((filter) => (
                  <label key={filter.key} className="min-w-0 w-full sm:min-w-[180px] sm:flex-1 text-xs font-medium text-domino-inkSoft">
                    {filter.fieldKey === 'stage' ? 'Étape' : filter.label}
                    {filter.kind === 'select' ? (
                      <select
                        value={draftFilters[filter.key] ?? ''}
                        onChange={(event) => setDraftFilters((current) => ({
                          ...current,
                          [filter.key]: event.target.value,
                        }))}
                        className="mt-1.5 h-9 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-domino-ink outline-none transition focus:border-gray-400 focus:ring-2 focus:ring-gray-100"
                      >
                        <option value="">Tous</option>
                        {filter.options?.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        value={draftFilters[filter.key] ?? ''}
                        placeholder={filter.placeholder}
                        onChange={(event) => setDraftFilters((current) => ({
                          ...current,
                          [filter.key]: event.target.value,
                        }))}
                        className="mt-1.5 bg-white"
                      />
                    )}
                  </label>
                ))}
                <Button onClick={applyFilters}>Appliquer</Button>
              </div>
            </details>
          )}

          <ToolResultPageRenderer
            composition={buildToolAppComposition(runtime.surface)}
            viewEnvelopesByInstanceId={runtime.viewEnvelopesByBindingId}
            pageMode="data"
            pageTitle={runtime.toolApp.title}
          />
          <div className="mx-4 sm:mx-5 lg:mx-6">
            <details className="border-t border-domino-cardBorder py-4">
              <summary className="cursor-pointer text-sm font-medium text-domino-inkSoft">Sources et fonctionnement</summary>
              <ul className="mt-3 space-y-2 text-sm text-domino-inkSoft">
                {Array.from(new Set(runtime.surface.views.map((binding) => {
                  const family = binding.sourcePlan?.family;
                  if (family === 'crm_query') return 'CRM — données accessibles à votre compte';
                  if (family === 'flow_snapshot_read') return 'Résultats enregistrés dans Domino';
                  if (family === 'static_view' || !family) return 'Contenu enregistré dans l’application';
                  return 'Historique des utilisations de Domino';
                }))).map((label) => <li key={label}>{label}</li>)}
              </ul>
              <p className="mt-3 text-xs text-domino-inkSoft">Actualiser recharge les sources disponibles. Cela ne garantit pas leur synchronisation avec le service d’origine.</p>
              {canManage && <button type="button" className="mt-3 text-sm font-medium text-domino-aubergine underline underline-offset-4" onClick={() => navigate('/connections')}>Gérer les connexions</button>}
            </details>
            <details className="border-t border-domino-cardBorder py-4">
              <summary className="cursor-pointer text-sm font-medium text-domino-inkSoft">Discussion</summary>
              <ToolAppCommunicationPanel toolAppKey={runtime.toolApp.key} />
            </details>
          </div>
        </div>
      </Card>
    </div>
  );
};
